import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { listEvaluators, createEvaluator, updateEvaluator, deleteEvaluator } from "../api";
import type { Evaluator } from "../types";

interface UseEvaluatorsOptions {
  selectedGroupId?: string;
}

export function useEvaluators({ selectedGroupId }: UseEvaluatorsOptions) {
  const navigate = useNavigate();

  const [evaluators, setEvaluators] = useState<Evaluator[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const selectedEvaluator =
    evaluators.find((e) => e.group_id === selectedGroupId) ?? null;

  const loadEvaluators = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listEvaluators();
      setEvaluators(data);
    } catch (error: any) {
      // The API's own detail is far more useful than a generic failure — duplicate
      // criteria and duplicate names both come back as actionable 409s.
      toast.error(error?.message || "Failed to load evaluators");
      console.error("Failed to load evaluators:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvaluators();
  }, [loadEvaluators]);

  const handleSelectEvaluator = useCallback(
    (groupId: string) => {
      setIsCreating(false);
      navigate(`/evaluators/${groupId}`);
    },
    [navigate]
  );

  const handleStartCreate = useCallback(() => {
    setIsCreating(true);
    navigate("/evaluators");
  }, [navigate]);

  const handleCancelCreate = useCallback(() => {
    setIsCreating(false);
  }, []);

  const handleCreate = useCallback(
    async (criteria: string, name?: string) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        // Omitting group_id keeps the endpoint's idempotent find-or-create behaviour and
        // lets the server generate a slug; sending one opts into the named path.
        const newEvaluator = await createEvaluator(
          name ? { criteria, group_id: name } : { criteria }
        );
        toast.success("Evaluator created");
        await loadEvaluators();
        setIsCreating(false);
        navigate(`/evaluators/${newEvaluator.group_id}`);
      } catch (error: any) {
        toast.error(error?.message || "Failed to create evaluator");
        console.error("Create evaluator error:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, loadEvaluators, navigate]
  );

  const handleUpdate = useCallback(
    async (id: string, criteria: string) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        const updated = await updateEvaluator(id, { criteria });
        toast.success("New version saved");
        await loadEvaluators();
        navigate(`/evaluators/${updated.group_id}`);
      } catch (error: any) {
        toast.error(error?.message || "Failed to update evaluator");
        console.error("Update evaluator error:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, loadEvaluators, navigate]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        await deleteEvaluator(id);
        toast.success("Evaluator deleted");
        await loadEvaluators();
        navigate("/evaluators");
      } catch (error: any) {
        toast.error(error?.message || "Failed to delete evaluator");
        console.error("Delete evaluator error:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, loadEvaluators, navigate]
  );

  return {
    evaluators,
    isLoading,
    isSubmitting,
    isCreating,
    selectedEvaluator,
    handleSelectEvaluator,
    handleStartCreate,
    handleCancelCreate,
    handleCreate,
    handleUpdate,
    handleDelete,
  };
}
